import React, { useState } from 'react';
import { ethers } from 'ethers'; // Correct import for ethers.js v5
import '98.css';
import './App.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CONTRACT_ADDRESS, ABI } from './contractConfig';

const App = () => {
  const [provider, setProvider] = useState(null);
  const [contract, setContract] = useState(null);
  const [account, setAccount] = useState(null);
  const [balance, setBalance] = useState(0);
  const [blockNumber, setBlockNumber] = useState(0);
  const [halvingCountdown, setHalvingCountdown] = useState('');
  const [miningCountdown, setMiningCountdown] = useState('');
  const [previousWinner, setPreviousWinner] = useState('');
  const [miners, setMiners] = useState([]);
  const [minersCount, setMinersCount] = useState(0);
  const [batchCount, setBatchCount] = useState(1);
  const [futureMineCounts, setFutureMineCounts] = useState(1);
  const [futureBlockCounts, setFutureBlockCounts] = useState(1);

  // State for showing/hiding modals
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showFutureModal, setShowFutureModal] = useState(false);

  // Manually connect MetaMask wallet
  const connectWallet = async () => {
    if (window.ethereum) {
      try {
        const prov = new ethers.providers.Web3Provider(window.ethereum);
        setProvider(prov);

        const accounts = await prov.send("eth_requestAccounts", []);
        setAccount(accounts[0]);

        // Check if MetaMask is connected to the correct network
        const network = await prov.getNetwork();
        if (network.chainId !== 1) { // Check for Ethereum Mainnet
          showAlert('Please connect to the Ethereum Mainnet', 'error');
          return;
        }

        const signer = prov.getSigner();
        const contractInstance = new ethers.Contract(CONTRACT_ADDRESS, ABI, signer);
        setContract(contractInstance);

        const balance = await contractInstance.balanceOf(accounts[0]);
        setBalance(ethers.utils.formatEther(balance));

        await updateBlockInfo(contractInstance);
        setInterval(() => updateBlockInfo(contractInstance), 1000); // Auto-update every second
      } catch (error) {
        showAlert("Failed to connect MetaMask", "error");
        console.error("Connection error:", error);
      }
    } else {
      showAlert("MetaMask not detected. Please install MetaMask.", "error");
    }
  };

  // Disconnect wallet
  const disconnectWallet = () => {
    setAccount(null);
    setContract(null);
    setProvider(null);
  };

  const updateBlockInfo = async (contractInstance) => {
    const blockNum = await contractInstance.blockNumber();
    setBlockNumber(blockNum.toString());

    const halvingBlock = await contractInstance.nextHalvingBlock();
    calculateHalvingCountdown(halvingBlock, blockNum);

    const blockTime = await contractInstance.lastBlockTime();
    calculateMiningCountdown(blockTime);

    const minerAddresses = await contractInstance.minersOfBlock(blockNum);
    const minerCount = await contractInstance.minersOfBlockCount(blockNum - 1);
    const previousBlockWinner = await contractInstance.selectedMinerOfBlock(blockNum - 1);

    setMiners(minerAddresses);
    setMinersCount(minerCount.toString());
    setPreviousWinner(previousBlockWinner.toLowerCase());
  };

  const calculateHalvingCountdown = (halvingBlock, currentBlock) => {
    const blocksLeft = halvingBlock - currentBlock;
    setHalvingCountdown(`${blocksLeft}`);
  };

  const calculateMiningCountdown = (lastBlockTime) => {
    const currentTime = Math.floor(Date.now() / 1000);
    const nextBlockTime = parseInt(lastBlockTime) + 60;
    const countdown = nextBlockTime - currentTime;
    if (countdown > 0) {
      setMiningCountdown(`${countdown}`);
    } else {
      setMiningCountdown(`Mining in progress...`);
    }
  };

  const handleMine = async () => {
    try {
      const tx = await contract.mine({ value: ethers.utils.parseEther("0.001") });
      await tx.wait();
      showAlert("Mining successful!", "success");
    } catch (error) {
      const errorMessage = error.reason || error.message || "Unknown error occurred.";
      showAlert(`Mining failed: ${errorMessage}`, "error");
    }
  };

  const handleMineBatch = async () => {
    try {
      const tx = await contract.mineBatch(batchCount, { value: ethers.utils.parseEther((0.001 * batchCount).toString()) });
      await tx.wait();
      showAlert(`Mining ${batchCount} times successful!`, "success");
      setShowBatchModal(false); // Close the modal after success
    } catch (error) {
      const errorMessage = error.reason || error.message || "Unknown error occurred.";
      showAlert(`Mining batch failed: ${errorMessage}`, "error");
    }
  };

  const handleFutureMine = async () => {
    try {
      const tx = await contract.futureMine(futureMineCounts, futureBlockCounts, { value: ethers.utils.parseEther((0.001 * futureMineCounts * futureBlockCounts).toString()) });
      await tx.wait();
      showAlert(`Mining ${futureMineCounts} times over ${futureBlockCounts} future blocks successful!`, "success");
      setShowFutureModal(false); // Close the modal after success
    } catch (error) {
      const errorMessage = error.reason || error.message || "Unknown error occurred.";
      showAlert(`Future mining failed: ${errorMessage}`, "error");
    }
  };

  const showAlert = (message, type) => {
    if (type === "success") {
      toast.success(message);
    } else {
      toast.error(message);
    }
  };

  const truncateAddress = (address) => {
    return address ? `${address.slice(0, 10)}...${address.slice(-10)}` : '';
  };

  return (
    <div className="container">
      <div className="header">
        <h3>BitToken Miner</h3>
        {!account ? (
          <button className="mine-button" onClick={connectWallet}>Connect MetaMask</button>
        ) : (
          <div>
            <button className="mine-button" onClick={disconnectWallet}>Disconnect Wallet</button>
          </div>
        )}
      </div>

      {account && contract ? (
        <div className="info-cards">
          <div className="card">
            <p className='title-bar'>Connected Account</p>
            <h3>{truncateAddress(account)}</h3>
          </div>
          <div className="card">
            <p className='title-bar'>Balance</p>
            <h3>{balance} BTT</h3>
          </div>
          <div className="card">
            <p className='title-bar'>Current Block Number</p>
            <h3>{blockNumber}</h3>
          </div>
          <div className="card">
            <p className='title-bar'>Blocks until next halving</p>
            <h3>{halvingCountdown}</h3>
          </div>
          <div className="card">
            <p className='title-bar'>Next block</p>
            <h3>{miningCountdown}</h3>
          </div>
          <div className="card">
            <p className='title-bar'>Total Miners</p>
            <h3>{minersCount}</h3>
          </div>
          <div className="card">
            <p className='title-bar'>Previous Winner for Block {blockNumber - 1}</p>
            <a href={`https://etherscan.io/address/${previousWinner}`} target="_blank" rel="noopener noreferrer">
              <h3>{truncateAddress(previousWinner !== '0x0000000000000000000000000000000000000000' ? previousWinner : "No winner yet")}</h3>
            </a>
          </div>
        </div>
      ) : (
        <p>Please connect your MetaMask wallet to access the contract data.</p>
      )}
      <div className="actions">
        <button className="mine-button" onClick={handleMine}>Mine BitToken</button>
        <button className="mine-button" onClick={() => setShowBatchModal(true)}>Batch Mine</button>
        <button className="mine-button" onClick={() => setShowFutureModal(true)}>Future Mine</button>
      </div>

      {/* Modal for Batch Mine */}
      {showBatchModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Batch Mine</h3>
            <label htmlFor="batchCount">Batch Count: </label>
            <input
              type="number"
              id="batchCount"
              value={batchCount}
              onChange={(e) => setBatchCount(parseInt(e.target.value) || 1)}
              min="1"
            />
            <button className="mine-button" onClick={handleMineBatch}>Mine Batch</button>
            <button className="mine-button" onClick={() => setShowBatchModal(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Modal for Future Mine */}
      {showFutureModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Future Mine</h3>
            <label htmlFor="futureMineCounts">Mine Counts: </label>
            <input
              type="number"
              id="futureMineCounts"
              value={futureMineCounts}
              onChange={(e) => setFutureMineCounts(parseInt(e.target.value) || 1)}
              min="1"
            />
            <label htmlFor="futureBlockCounts">Block Counts: </label>
            <input
              type="number"
              id="futureBlockCounts"
              value={futureBlockCounts}
              onChange={(e) => setFutureBlockCounts(parseInt(e.target.value) || 1)}
              min="1"
            />
            <button className="mine-button" onClick={handleFutureMine}>Future Mine</button>
            <button className="mine-button" onClick={() => setShowFutureModal(false)}>Close</button>
          </div>
        </div>
      )}

      <div className="miner-table-wrapper">
        <div className="miner-table-container">
          <table className="miner-table">
            <thead>
              <tr>
                <th className='title-bar'>Miner Address</th>
              </tr>
            </thead>
            <tbody>
              {miners.map((miner, index) => (
                <tr key={index}>
                  <td>
                    <a href={`https://etherscan.io/address/${miner}`} target="_blank" rel="noopener noreferrer">
                      {miner}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default App;
